import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { SubscriptionGate } from "@/components/SubscriptionGate";
import { CreditConfirmDialog } from "@/components/CreditConfirmDialog";
import { toast } from "sonner";

import {
  Search,
  Sparkles,
  Building2,
  TrendingUp,
  Users,
  DollarSign,
  Briefcase,
  MessageSquare,
  Heart,
  Award,
  BarChart3,
  Newspaper,
  Target,
  ArrowRight,
  ExternalLink,
  Bookmark,
  Download,
  Share2,
  Loader2,
  X,
} from "lucide-react";
import { Helmet } from "react-helmet-async";
import { DashboardLayout } from "@/components/DashboardLayout";
import { useJobContext } from "@/contexts/JobContext";

interface CompanyData {
  name: string;
  logo?: string;
  industry: string;
  size: string;
  founded?: string;
  headquarters: string;
  website?: string;
  overview: string;
  culture: any;
  interview: any;
  compensation: any;
  news: any[];
  growth: any;
  diversity: any;
  strategy: any;
  similarCompanies: any[];
  openPositions: any[];
}

export default function CompanyInsights() {
  const { selectedJob, clearSelectedJob } = useJobContext();
  const [searchQuery, setSearchQuery] = useState("");
  const [jobUrl, setJobUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingSearch, setPendingSearch] = useState<string>("");
  const [recentSearches, setRecentSearches] = useState<string[]>([
    "Google",
    "Amazon",
    "Microsoft",
    "Apple",
    "Tesla",
  ]);
  const navigate = useNavigate();

  // Auto-populate from JobContext
  useEffect(() => {
    if (selectedJob?.company) {
      setSearchQuery(selectedJob.company);
      toast.success(`Researching: ${selectedJob.company}`);
    }
  }, [selectedJob]);

  const trendingCompanies = [
    "Google",
    "Amazon",
    "Microsoft",
    "Apple",
    "Tesla",
    "Netflix",
    "Meta",
    "Salesforce",
  ];

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error("Please enter a company name");
      return;
    }

    // Show confirmation dialog for premium feature
    setPendingSearch(searchQuery);
    setShowConfirm(true);
  };

  const executeSearch = async () => {
    const companyToSearch = pendingSearch;
    setShowConfirm(false);
    setIsLoading(true);

    try {
      // Generate domain hash for idempotency
      const encoder = new TextEncoder();
      const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(companyToSearch.toLowerCase()));
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const domain = hashArray.map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
      const refId = `company:${domain}:${Date.now()}`;

      // Phase 1: Charge credits atomically (5 credits, premium only)
      const { data: creditResult, error: creditError } = await supabase.functions.invoke('credits-spend', {
        body: { featureKey: 'company.insights', refId }
      });

      if (creditError) {
        const errorData = creditError as any;
        if (errorData.error === 'PREMIUM_REQUIRED') {
          toast.error(errorData.message || 'Company Insights requires Premium plan', {
            action: { label: 'Upgrade', onClick: () => navigate('/pricing') },
          });
          return;
        }
        if (errorData.error === 'INSUFFICIENT_CREDITS') {
          toast.error(errorData.message || 'Not enough credits', {
            action: { label: 'Get Credits', onClick: () => navigate('/pricing') },
          });
          return;
        }
        throw creditError;
      }

      // Phase 2: Research company
      const { data, error } = await supabase.functions.invoke("research-company", {
        body: { companyName: companyToSearch },
      });

      if (error) throw error;

      setCompanyData(data);
      
      // Add to recent searches
      setRecentSearches(prev => {
        const updated = [companyToSearch, ...prev.filter(s => s !== companyToSearch)];
        return updated.slice(0, 5);
      });

      if (!creditResult.idempotent && creditResult.cost > 0) {
        toast.success(`Company insights generated! Research complete for ${companyToSearch}. Used ${creditResult.cost} credits. ${creditResult.remaining} remaining.`);
      } else {
        toast.success(`Company insights generated! Research complete for ${companyToSearch}`);
      }

      // Refresh credits display
      window.dispatchEvent(new CustomEvent('creditsUpdated'));
    } catch (error: any) {
      console.error("[COMPANY-INSIGHTS] Error:", error);
      toast.error(error.message || "Failed to research company. Please try again");
    } finally {
      setIsLoading(false);
      setPendingSearch("");
    }
  };

  const handleJobUrlImport = async () => {
    if (!jobUrl.trim()) {
      toast.error("Please enter a job URL");
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("extract-company-from-url", {
        body: { jobUrl },
      });

      if (error) throw error;

      setSearchQuery(data.companyName);
      await handleSearch();
    } catch (error: any) {
      console.error("Error extracting company:", error);
      toast.error(error.message || "Failed to extract company. Please try again");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Company Insights - AI-Powered Company Research | LoopBaan.ai</title>
        <meta
          name="description"
          content="Get comprehensive AI-powered insights about companies including culture, salaries, interview tips, and more."
        />
      </Helmet>

      <DashboardLayout>
        <SubscriptionGate
          requiredPlan="pro"
          feature="Company Insights"
          description="Get AI-powered company research including culture insights, salary data, interview tips, and comprehensive company intelligence."
        >
          <div className="p-6 space-y-6">
          {/* Hero Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge className="bg-accent/20 text-accent border-accent/30">
                <Sparkles className="h-3 w-3 mr-1" />
                Premium Feature
              </Badge>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Company Insights</h1>
              <p className="text-muted-foreground">
                AI-powered company research to help you apply smarter and interview better
              </p>
            </div>
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-accent" />
                <span className="text-sm">10,000+ companies analyzed</span>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-accent" />
                <span className="text-sm">Real-time data</span>
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-accent" />
                <span className="text-sm">AI-powered insights</span>
              </div>
            </div>
          </div>

          {/* Search Section */}
          <Card className="p-6 space-y-6">
            {selectedJob && (
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 mb-4 flex justify-between items-center">
                <div>
                  <p className="text-sm">
                    Researching: <strong>{selectedJob.company}</strong>
                  </p>
                  {selectedJob.title && (
                    <p className="text-xs text-muted-foreground mt-1">From job: {selectedJob.title}</p>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={clearSelectedJob}>
                  <X className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              </div>
            )}
            
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Search for a company</label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Search for a company... (e.g., Google, Amazon, Tesla)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    className="flex-1"
                  />
                  <Button onClick={handleSearch} disabled={isLoading}>
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Search className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Or</span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Paste a job posting URL</label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Paste job URL from LinkedIn, Indeed, Glassdoor..."
                    value={jobUrl}
                    onChange={(e) => setJobUrl(e.target.value)}
                    className="flex-1"
                  />
                  <Button onClick={handleJobUrlImport} disabled={isLoading} variant="outline">
                    Extract Info
                  </Button>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">Trending companies</p>
              <div className="flex flex-wrap gap-2">
                {trendingCompanies.map((company) => (
                  <Badge
                    key={company}
                    variant="outline"
                    className="cursor-pointer hover:bg-accent hover:text-accent-foreground"
                    onClick={() => {
                      setSearchQuery(company);
                      setTimeout(handleSearch, 100);
                    }}
                  >
                    {company}
                  </Badge>
                ))}
              </div>
            </div>

            {recentSearches.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Recent searches</p>
                <div className="flex flex-wrap gap-2">
                  {recentSearches.map((company) => (
                    <Badge
                      key={company}
                      variant="secondary"
                      className="cursor-pointer hover:bg-secondary/80"
                      onClick={() => {
                        setSearchQuery(company);
                        setTimeout(handleSearch, 100);
                      }}
                    >
                      {company}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Company Report */}
          {companyData && (
            <div className="space-y-6">
              {/* Company Header */}
              <Card className="p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-shrink-0">
                    {companyData.logo ? (
                      <img
                        src={companyData.logo}
                        alt={companyData.name}
                        className="h-24 w-24 rounded-lg object-contain"
                      />
                    ) : (
                      <div className="h-24 w-24 rounded-lg bg-muted flex items-center justify-center">
                        <Building2 className="h-12 w-12 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-4">
                    <div>
                      <h2 className="text-3xl font-bold mb-2">{companyData.name}</h2>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <Badge>{companyData.industry}</Badge>
                        <Badge variant="outline">{companyData.size} employees</Badge>
                        {companyData.founded && (
                          <Badge variant="outline">Founded {companyData.founded}</Badge>
                        )}
                      </div>
                      <p className="text-muted-foreground">{companyData.headquarters}</p>
                      {companyData.website && (
                        <a
                          href={companyData.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent hover:underline flex items-center gap-1 mt-2"
                        >
                          Visit website <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm">
                        <Bookmark className="h-4 w-4 mr-2" />
                        Save to Favorites
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => navigate("/job-search")}>
                        <Briefcase className="h-4 w-4 mr-2" />
                        View Open Jobs
                      </Button>
                      <Button variant="outline" size="sm">
                        <Download className="h-4 w-4 mr-2" />
                        Download Report
                      </Button>
                      <Button variant="outline" size="sm">
                        <Share2 className="h-4 w-4 mr-2" />
                        Share
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Insights Tabs */}
              <Tabs defaultValue="overview" className="space-y-6">
                <TabsList className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 h-auto">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="culture">Culture</TabsTrigger>
                  <TabsTrigger value="interview">Interview</TabsTrigger>
                  <TabsTrigger value="compensation">Compensation</TabsTrigger>
                  <TabsTrigger value="news">News</TabsTrigger>
                  <TabsTrigger value="growth">Growth</TabsTrigger>
                  <TabsTrigger value="diversity">Diversity</TabsTrigger>
                  <TabsTrigger value="strategy">Strategy</TabsTrigger>
                  <TabsTrigger value="similar">Similar</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-6">
                  <Card className="p-6">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-accent" />
                      Company Overview
                    </h3>
                    <div className="prose max-w-none">
                      <p className="text-muted-foreground whitespace-pre-line">
                        {companyData.overview}
                      </p>
                    </div>
                  </Card>
                </TabsContent>

                <TabsContent value="culture" className="space-y-6">
                  <Card className="p-6">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <Users className="h-5 w-5 text-accent" />
                      Company Culture & Values
                    </h3>
                    {companyData.culture && (
                      <div className="space-y-6">
                        <div>
                          <h4 className="font-semibold mb-2">Mission & Values</h4>
                          <p className="text-muted-foreground">{companyData.culture.mission}</p>
                        </div>
                        {companyData.culture.ratings && (
                          <div>
                            <h4 className="font-semibold mb-3">Employee Ratings</h4>
                            <div className="grid gap-3">
                              {Object.entries(companyData.culture.ratings).map(([key, value]: any) => (
                                <div key={key} className="flex items-center justify-between">
                                  <span className="text-sm capitalize">
                                    {key.replace(/([A-Z])/g, " $1").trim()}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-accent"
                                        style={{ width: `${(value / 5) * 100}%` }}
                                      />
                                    </div>
                                    <span className="text-sm font-medium w-12">{value}/5</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {(companyData.culture.pros || companyData.culture.cons) && (
                          <div className="grid md:grid-cols-2 gap-6">
                            {companyData.culture.pros && (
                              <div>
                                <h4 className="font-semibold mb-3 text-green-600">Pros</h4>
                                <ul className="space-y-2">
                                  {companyData.culture.pros.map((pro: string, i: number) => (
                                    <li key={i} className="flex items-start gap-2 text-sm">
                                      <span className="text-green-600 mt-0.5">✓</span>
                                      <span>{pro}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {companyData.culture.cons && (
                              <div>
                                <h4 className="font-semibold mb-3 text-amber-600">Cons</h4>
                                <ul className="space-y-2">
                                  {companyData.culture.cons.map((con: string, i: number) => (
                                    <li key={i} className="flex items-start gap-2 text-sm">
                                      <span className="text-amber-600 mt-0.5">⚠</span>
                                      <span>{con}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                </TabsContent>

                <TabsContent value="interview" className="space-y-6">
                  <Card className="p-6">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <MessageSquare className="h-5 w-5 text-accent" />
                      Interview Process & Tips
                    </h3>
                    {companyData.interview && (
                      <div className="space-y-6">
                        {companyData.interview.process && (
                          <div>
                            <h4 className="font-semibold mb-3">Typical Interview Process</h4>
                            <ol className="space-y-3">
                              {companyData.interview.process.map((step: string, i: number) => (
                                <li key={i} className="flex gap-3">
                                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-sm">
                                    {i + 1}
                                  </span>
                                  <span className="text-sm pt-0.5">{step}</span>
                                </li>
                              ))}
                            </ol>
                          </div>
                        )}
                        {companyData.interview.questions && (
                          <div>
                            <h4 className="font-semibold mb-3">Common Interview Questions</h4>
                            <div className="space-y-3">
                              {companyData.interview.questions.map((question: string, i: number) => (
                                <div key={i} className="flex items-start justify-between gap-4 p-3 rounded-lg bg-muted/50">
                                  <span className="text-sm flex-1">{question}</span>
                                  <Button size="sm" variant="ghost" onClick={() => navigate("/interview-practice")}>
                                    Practice
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {companyData.interview.tips && (
                          <div>
                            <h4 className="font-semibold mb-3">Interview Tips</h4>
                            <ul className="space-y-2">
                              {companyData.interview.tips.map((tip: string, i: number) => (
                                <li key={i} className="flex items-start gap-2 text-sm">
                                  <span className="text-accent mt-0.5">📌</span>
                                  <span>{tip}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                </TabsContent>

                <TabsContent value="compensation" className="space-y-6">
                  <Card className="p-6">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <DollarSign className="h-5 w-5 text-accent" />
                      Compensation & Benefits
                    </h3>
                    {companyData.compensation && (
                      <div className="space-y-6">
                        {companyData.compensation.salaries && (
                          <div>
                            <h4 className="font-semibold mb-3">Salary Ranges by Role</h4>
                            <div className="grid gap-3">
                              {Object.entries(companyData.compensation.salaries).map(([role, salary]: any) => (
                                <div key={role} className="flex justify-between items-center p-3 rounded-lg bg-muted/50">
                                  <span className="text-sm font-medium">{role}</span>
                                  <span className="text-sm text-accent">{salary}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {companyData.compensation.benefits && (
                          <div>
                            <h4 className="font-semibold mb-3">Benefits Package</h4>
                            <div className="grid md:grid-cols-2 gap-3">
                              {companyData.compensation.benefits.map((benefit: string, i: number) => (
                                <div key={i} className="flex items-center gap-2 text-sm">
                                  <span className="text-accent">✓</span>
                                  <span>{benefit}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                </TabsContent>

                <TabsContent value="news" className="space-y-6">
                  <Card className="p-6">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <Newspaper className="h-5 w-5 text-accent" />
                      Recent News & Updates
                    </h3>
                    {companyData.news && companyData.news.length > 0 ? (
                      <div className="space-y-4">
                        {companyData.news.map((item: any, i: number) => (
                          <div key={i} className="p-4 rounded-lg border space-y-2">
                            <div className="flex items-start justify-between gap-4">
                              <h4 className="font-semibold flex-1">{item.title}</h4>
                              {item.sentiment && (
                                <Badge variant={item.sentiment === "positive" ? "default" : "secondary"}>
                                  {item.sentiment}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{item.summary}</p>
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                              <span>{item.source} • {item.date}</span>
                              {item.url && (
                                <a
                                  href={item.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-accent hover:underline flex items-center gap-1"
                                >
                                  Read more <ExternalLink className="h-3 w-3" />
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground">No recent news available</p>
                    )}
                  </Card>
                </TabsContent>

                <TabsContent value="growth" className="space-y-6">
                  <Card className="p-6">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-accent" />
                      Growth & Stability
                    </h3>
                    {companyData.growth && (
                      <div className="space-y-6">
                        {companyData.growth.healthScore && (
                          <div className="text-center p-6 rounded-lg bg-muted/50">
                            <div className="text-5xl font-bold text-accent mb-2">
                              {companyData.growth.healthScore}/100
                            </div>
                            <p className="text-sm text-muted-foreground">Company Health Score</p>
                          </div>
                        )}
                        {companyData.growth.indicators && (
                          <div>
                            <h4 className="font-semibold mb-3">Growth Indicators</h4>
                            <div className="grid gap-3">
                              {Object.entries(companyData.growth.indicators).map(([key, value]: any) => (
                                <div key={key} className="flex justify-between items-center">
                                  <span className="text-sm capitalize">
                                    {key.replace(/([A-Z])/g, " $1").trim()}
                                  </span>
                                  <span className="text-sm font-medium">{value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {companyData.growth.recommendation && (
                          <div className="p-4 rounded-lg bg-accent/10 border border-accent/20">
                            <p className="text-sm">{companyData.growth.recommendation}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                </TabsContent>

                <TabsContent value="diversity" className="space-y-6">
                  <Card className="p-6">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <Heart className="h-5 w-5 text-accent" />
                      Diversity & Inclusion
                    </h3>
                    {companyData.diversity && (
                      <div className="space-y-6">
                        {companyData.diversity.statistics && (
                          <div>
                            <h4 className="font-semibold mb-3">D&I Statistics</h4>
                            <div className="grid gap-3">
                              {Object.entries(companyData.diversity.statistics).map(([key, value]: any) => (
                                <div key={key} className="flex justify-between items-center">
                                  <span className="text-sm capitalize">
                                    {key.replace(/([A-Z])/g, " $1").trim()}
                                  </span>
                                  <span className="text-sm font-medium">{value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {companyData.diversity.awards && (
                          <div>
                            <h4 className="font-semibold mb-3">Awards & Recognition</h4>
                            <ul className="space-y-2">
                              {companyData.diversity.awards.map((award: string, i: number) => (
                                <li key={i} className="flex items-start gap-2 text-sm">
                                  <Award className="h-4 w-4 text-accent mt-0.5" />
                                  <span>{award}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                </TabsContent>

                <TabsContent value="strategy" className="space-y-6">
                  <Card className="p-6">
                    <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <Target className="h-5 w-5 text-accent" />
                      Application Strategy
                    </h3>
                    {companyData.strategy && (
                      <div className="space-y-6">
                        {companyData.strategy.bestTime && (
                          <div>
                            <h4 className="font-semibold mb-2">Best Time to Apply</h4>
                            <p className="text-sm text-muted-foreground">{companyData.strategy.bestTime}</p>
                          </div>
                        )}
                        {companyData.strategy.standOut && (
                          <div>
                            <h4 className="font-semibold mb-3">How to Stand Out</h4>
                            <ul className="space-y-2">
                              {companyData.strategy.standOut.map((tip: string, i: number) => (
                                <li key={i} className="flex items-start gap-2 text-sm">
                                  <span className="text-accent mt-0.5">→</span>
                                  <span>{tip}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {companyData.strategy.checklist && (
                          <div>
                            <h4 className="font-semibold mb-3">Application Checklist</h4>
                            <div className="space-y-2">
                              {companyData.strategy.checklist.map((item: string, i: number) => (
                                <div key={i} className="flex items-center gap-2 text-sm">
                                  <input type="checkbox" className="rounded" />
                                  <span>{item}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                </TabsContent>

                <TabsContent value="similar" className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold mb-4">Similar Companies</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {companyData.similarCompanies && companyData.similarCompanies.length > 0 ? (
                        companyData.similarCompanies.map((company: any, i: number) => (
                          <Card key={i} className="p-4 hover:shadow-lg transition-shadow cursor-pointer">
                            <div className="space-y-3">
                              <div className="flex items-start justify-between">
                                <h4 className="font-semibold">{company.name}</h4>
                                {company.hiring && (
                                  <Badge variant="outline" className="text-xs">
                                    Hiring
                                  </Badge>
                                )}
                              </div>
                              <div className="space-y-1 text-sm text-muted-foreground">
                                <p>{company.industry}</p>
                                <p>{company.size} employees</p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="w-full"
                                onClick={() => {
                                  setSearchQuery(company.name);
                                  setTimeout(handleSearch, 100);
                                }}
                              >
                                View Insights
                              </Button>
                            </div>
                          </Card>
                        ))
                      ) : (
                        <p className="text-muted-foreground col-span-full">No similar companies found</p>
                      )}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              {/* Open Positions */}
              {companyData.openPositions && companyData.openPositions.length > 0 && (
                <Card className="p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-accent" />
                    Open Positions at {companyData.name}
                  </h3>
                  <div className="space-y-3 mb-4">
                    {companyData.openPositions.slice(0, 5).map((job: any, i: number) => (
                      <div key={i} className="flex items-center justify-between p-4 rounded-lg border">
                        <div>
                          <h4 className="font-semibold">{job.title}</h4>
                          <p className="text-sm text-muted-foreground">
                            {job.location} • {job.posted}
                          </p>
                        </div>
                        <Button size="sm">View Details</Button>
                      </div>
                    ))}
                  </div>
                  <Button className="w-full" variant="outline" onClick={() => navigate("/job-search")}>
                    View All {companyData.openPositions.length} Open Positions
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Card>
              )}
            </div>
          )}
          </div>
        </SubscriptionGate>

        {/* Premium Feature Confirmation Dialog */}
        <CreditConfirmDialog
          open={showConfirm}
          onOpenChange={setShowConfirm}
          onConfirm={executeSearch}
          credits={5}
          featureName="Company Insights"
          loading={isLoading}
          isPremiumFeature={true}
        />
      </DashboardLayout>
    </>
  );
}
